// Utility functions for cookie management
import { ENV } from '@/config/environment';

export interface CookieOptions {
  expires?: Date | number; // Date object or days from now
  path?: string;
  domain?: string;
  secure?: boolean;
  sameSite?: 'strict' | 'lax' | 'none';
}

export class CookieManager {
  /**
   * Set a cookie with optional configuration
   */
  static set(name: string, value: string, options: CookieOptions = {}): void {
    if (typeof document === 'undefined') return;

    const {
      expires,
      path = '/',
      domain,
      secure = ENV.app.isProduction, // Secure flag in production (HTTPS required)
      sameSite = 'lax'
    } = options;

    let cookieString = `${encodeURIComponent(name)}=${encodeURIComponent(value)}`;

    if (expires) {
      const expiresDate = typeof expires === 'number' 
        ? new Date(Date.now() + expires * 24 * 60 * 60 * 1000) // Convert days to milliseconds
        : expires;
      cookieString += `; expires=${expiresDate.toUTCString()}`;
    }

    if (path) {
      cookieString += `; path=${path}`;
    }

    if (domain) {
      cookieString += `; domain=${domain}`;
    }

    if (secure) {
      cookieString += `; secure`;
    }

    cookieString += `; samesite=${sameSite}`;

    document.cookie = cookieString;
  }

  /**
   * Get a cookie value by name
   */
  static get(name: string): string | null {
    if (typeof document === 'undefined') return null;

    const nameEQ = name + '=';
    const cookies = document.cookie.split(';');

    for (let cookie of cookies) {
      cookie = cookie.trim();
      if (cookie.indexOf(nameEQ) === 0) {
        return decodeURIComponent(cookie.substring(nameEQ.length));
      }
    }

    return null;
  }

  /**
   * Remove a cookie by setting it to expire in the past
   */
  static remove(name: string, options: Omit<CookieOptions, 'expires'> = {}): void {
    CookieManager.set(name, '', {
      ...options,
      expires: new Date(0) // Set to epoch to delete
    });
  }

  /**
   * Check if a cookie exists
   */
  static exists(name: string): boolean {
    return CookieManager.get(name) !== null;
  }

  /**
   * Get all cookies as an object
   */
  static getAll(): Record<string, string> {
    if (typeof document === 'undefined') return {};

    const cookies: Record<string, string> = {};
    const cookieString = document.cookie;

    if (cookieString) {
      cookieString.split(';').forEach(cookie => {
        const [name, value] = cookie.trim().split('=');
        if (name && value) {
          cookies[decodeURIComponent(name)] = decodeURIComponent(value);
        }
      });
    }

    return cookies;
  }
}

// Theme-specific cookie utilities
export class ThemeCookieManager {
  private static readonly THEME_COOKIE_NAME = 'aditas-theme';
  private static readonly THEME_COOKIE_EXPIRES = 365; // 1 year

  static saveTheme(mode: string, variant: string): void {
    const themeData = { mode, variant };
    CookieManager.set(
      this.THEME_COOKIE_NAME, 
      JSON.stringify(themeData),
      {
        expires: this.THEME_COOKIE_EXPIRES,
        path: '/',
        sameSite: 'lax'
      }
    );
  }

  static loadTheme(): { mode?: string; variant?: string } | null {
    try {
      const themeString = CookieManager.get(this.THEME_COOKIE_NAME);
      if (themeString) {
        return JSON.parse(themeString);
      }
    } catch {
      // Silently clean up corrupted cookie
      this.clearTheme();
    }
    return null;
  }

  static clearTheme(): void {
    CookieManager.remove(this.THEME_COOKIE_NAME, { path: '/' });
  }
}
